import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useLocale } from '../context';
import { useSiteContent } from '../context/SiteContentContext';
import { useIsMobile } from '../hooks/useIsMobile';
import Logo from './Logo';
import './support-messenger.css';

const GUEST_KEY = 'vb_support_guest';
const THREAD_KEY = 'vb_support_thread';
const PRECHAT_KEY = 'vb_support_prechat';
const SOUND_KEY = 'vb_support_sound';
const FOLLOWUP_KEY = 'vb_support_followup';
const ACTIVITY_KEY = 'vb_support_activity';
const OPEN_EVENT = 'vb-support-open';
const OPEN_REGISTER_EVENT = 'vb-open-register';
const OPEN_REGISTER_OPTIONS = new Set([
  'Kayıt sayfasını aç',
  'Open the registration page',
]);
const POLL_MS = 2500;
const IDLE_CHECK_MS = 30000;
const DEFAULT_IDLE_MINUTES = 20;
const SCRIPT_TYPING_MS = 700;
const SCRIPT_GAP_MS = 280;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
]);

/** BlindLuck-style category chips */
const TOPICS_TR = [
  { value: 'register', label: 'Kayıt Ol' },
  { value: 'withdraw', label: 'Çekme' },
  { value: 'deposit', label: 'Yatırma' },
  { value: 'verification', label: 'Doğrulama' },
  { value: 'account', label: 'Hesabım' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'casino', label: 'Casino' },
  { value: 'sports', label: 'Spor' },
];

const TOPICS_EN = [
  { value: 'register', label: 'Sign Up' },
  { value: 'withdraw', label: 'Withdrawal' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'verification', label: 'Verification' },
  { value: 'account', label: 'My Account' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'casino', label: 'Casino' },
  { value: 'sports', label: 'Sports' },
];

const INTRO_TR = [
  'Merhaba! Ben VurgunBet Destek asistanınız.',
  'Hadi başlayalım. Bugün size nasıl yardımcı olabilirim?',
];

const INTRO_EN = [
  "Hey there! I'm your VurgunBet support assistant.",
  "Let's get started. What can I do for you today?",
];

function isCategoryPromptLine(text) {
  const t = String(text || '')
    .trim()
    .replace(/:+$/, '')
    .trim()
    .toLowerCase();
  if (!t) return false;
  return t === 'lütfen bir kategori seçin' || t === 'please select a category';
}

function filterIntroBubbles(list) {
  return (list || [])
    .map((b) => String(b || '').trim())
    .filter((b) => b && !isCategoryPromptLine(b));
}

const FOLLOWUPS = {
  register: {
    prompt_tr: 'Kayıt hakkında ne öğrenmek istersiniz?',
    prompt_en: 'What would you like to know about registration?',
    auto_reply_tr: `VurgunBet hesabı açmak için adımlar:

1) Ana sayfada veya üst menüde «Kayıt Ol»a tıklayın.
2) Kullanıcı adı, e-posta, cep telefonu ve şifrenizi girin.
3) Doğum tarihinizi ve cinsiyetinizi seçin.
4) 18+ olduğunuzu ve kullanım şartlarını kabul edin.
5) SMS ile gelen doğrulama kodunu girerek kaydı tamamlayın.

Kayıt olma seçenekleri:
• Site formu (e-posta / telefon + şifre)
• Google ile hızlı kayıt
• Telegram ile hızlı kayıt

Hesabınız oluşur. Takıldığınız yerde buradan yazabilirsiniz.`,
    auto_reply_en: `To create a VurgunBet account:

1) Tap «Sign Up» on the home page or in the top menu.
2) Enter a username, email, mobile phone and password.
3) Select your date of birth and gender.
4) Confirm you are 18+ and accept the terms.
5) Enter the SMS verification code to finish.

Registration options:
• Website form (email / phone + password)
• Quick sign-up with Google
• Quick sign-up with Telegram

Your account is then ready. If you get stuck, message us here.`,
    options_tr: [
      'Kayıt sayfasını aç',
      'SMS kodu gelmedi',
      'Kayıt sırasında hata alıyorum',
      'Kimlik doğrulama nasıl yapılır?',
      'Diğer',
    ],
    options_en: [
      'Open the registration page',
      'I did not receive the SMS code',
      'I get an error while registering',
      'How do I verify my identity?',
      'Other',
    ],
  },
  withdraw: {
    prompt_tr: 'Lütfen talebinizin türünü seçin.',
    prompt_en: 'Please, choose the type of your request.',
    options_tr: [
      'Çekim hakkında sorum var',
      'Nasıl çekim yaparım?',
      'Hangi ödeme yöntemlerini kabul ediyorsunuz?',
      'Çekim limitleri nelerdir?',
      'Diğer',
    ],
    options_en: [
      'I have questions regarding my withdrawal',
      'How do I make a withdrawal?',
      'What payment methods do you accept?',
      'What are the withdrawal limits?',
      'Other',
    ],
  },
  deposit: {
    prompt_tr: 'Lütfen talebinizin türünü seçin.',
    prompt_en: 'Please, choose the type of your request.',
    options_tr: [
      'Yatırma hakkında sorum var',
      'Nasıl para yatırırım?',
      'Hangi ödeme yöntemlerini kabul ediyorsunuz?',
      'Yatırma limitleri nelerdir?',
      'Diğer',
    ],
    options_en: [
      'I have questions regarding my deposit',
      'How do I make a deposit?',
      'What payment methods do you accept?',
      'What are the deposit limits?',
      'Other',
    ],
  },
  verification: {
    prompt_tr: 'Lütfen talebinizin türünü seçin.',
    prompt_en: 'Please, choose the type of your request.',
    options_tr: [
      'Hesap doğrulama nasıl yapılır?',
      'Belgelerimi gönderdim, durum nedir?',
      'Hangi belgeler gerekli?',
      'Diğer',
    ],
    options_en: [
      'How do I verify my account?',
      'I submitted documents — what is the status?',
      'Which documents are required?',
      'Other',
    ],
  },
  account: {
    prompt_tr: 'Lütfen talebinizin türünü seçin.',
    prompt_en: 'Please, choose the type of your request.',
    options_tr: [
      'Giriş / şifre sorunu',
      'Hesap bilgilerimi güncellemek istiyorum',
      'Hesabım kısıtlandı',
      'Diğer',
    ],
    options_en: [
      'Login / password issue',
      'I want to update my account details',
      'My account is restricted',
      'Other',
    ],
  },
  bonus: {
    prompt_tr: 'Lütfen talebinizin türünü seçin.',
    prompt_en: 'Please, choose the type of your request.',
    options_tr: [
      'Bonus nasıl alınır?',
      'Bonusum yüklenmedi',
      'Çevrim şartları nedir?',
      'Diğer',
    ],
    options_en: [
      'How do I claim a bonus?',
      'My bonus was not credited',
      'What are the wagering requirements?',
      'Other',
    ],
  },
  casino: {
    prompt_tr: 'Lütfen talebinizin türünü seçin.',
    prompt_en: 'Please, choose the type of your request.',
    options_tr: [
      'Oyun açılmıyor / teknik sorun',
      'Bahis / kazanç sorunu',
      'Diğer',
    ],
    options_en: [
      'Game will not open / technical issue',
      'Bet / win issue',
      'Other',
    ],
  },
  sports: {
    prompt_tr: 'Lütfen talebinizin türünü seçin.',
    prompt_en: 'Please, choose the type of your request.',
    options_tr: [
      'Kupon / bahis sorunu',
      'Sonuç / kazanç sorunu',
      'Diğer',
    ],
    options_en: [
      'Bet slip / wagering issue',
      'Result / settlement issue',
      'Other',
    ],
  },
};

function ensureGuestKey() {
  try {
    let key = localStorage.getItem(GUEST_KEY);
    if (!key || key.length < 12) {
      key = `g_${crypto.randomUUID().replace(/-/g, '')}`;
      localStorage.setItem(GUEST_KEY, key);
    }
    return key;
  } catch {
    return `g_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}

function loadThreadId() {
  try {
    return localStorage.getItem(THREAD_KEY) || '';
  } catch {
    return '';
  }
}

function saveThreadId(id) {
  try {
    if (id) localStorage.setItem(THREAD_KEY, id);
    else localStorage.removeItem(THREAD_KEY);
  } catch {
    /* ignore */
  }
}

function loadPrechat() {
  try {
    const raw = sessionStorage.getItem(PRECHAT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      name: String(parsed.name || '').trim(),
      email: String(parsed.email || '').trim(),
      topic: String(parsed.topic || '').trim(),
    };
  } catch {
    return null;
  }
}

function savePrechat(data) {
  try {
    sessionStorage.setItem(PRECHAT_KEY, JSON.stringify(data || {}));
  } catch {
    /* ignore */
  }
}

function loadFollowupDone() {
  try {
    return localStorage.getItem(FOLLOWUP_KEY) === '1';
  } catch {
    return false;
  }
}

function saveFollowupDone(done) {
  try {
    if (done) localStorage.setItem(FOLLOWUP_KEY, '1');
    else localStorage.removeItem(FOLLOWUP_KEY);
  } catch {
    /* ignore */
  }
}

function loadSoundEnabled() {
  try {
    const v = localStorage.getItem(SOUND_KEY);
    if (v == null) return true;
    return v !== '0';
  } catch {
    return true;
  }
}

function saveSoundEnabled(on) {
  try {
    localStorage.setItem(SOUND_KEY, on ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function loadActivityAt() {
  try {
    const n = Number(localStorage.getItem(ACTIVITY_KEY));
    if (Number.isFinite(n) && n > 0) return n;
  } catch {
    /* ignore */
  }
  return Date.now();
}

function saveActivityAt(ts) {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(ts || Date.now()));
  } catch {
    /* ignore */
  }
}

function clearActivityAt() {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
}

function inferFollowupDone(messages, prechat, topicLabels) {
  if (loadFollowupDone()) return true;
  const topic = String(prechat?.topic || '').trim();
  if (!topic) return false;
  const name = String(prechat?.name || '').trim();
  const email = String(prechat?.email || '').trim();
  if (name && email && isValidEmail(email)) return true;
  const userMsgs = (messages || []).filter(
    (m) => m.sender_type === 'user' && !(isTopicEcho(m.body) && !m.attachment),
  );
  if (userMsgs.length >= 2) return true;
  if (userMsgs.length === 1) {
    const body = String(userMsgs[0].body || '').trim();
    if (topicLabels instanceof Set && topicLabels.has(body)) return false;
    return true;
  }
  return false;
}

function authHeaders(token, guestKey, json = true) {
  const h = { 'X-Support-Guest-Key': guestKey };
  if (json) h['Content-Type'] = 'application/json';
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function parseDate(value) {
  if (value instanceof Date) return value;
  if (value == null || value === '') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function istanbulDateKey(d) {
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
}

function formatTime(value) {
  try {
    const d = parseDate(value);
    if (!d) return '';
    return d.toLocaleTimeString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
  } catch {
    return '';
  }
}

function formatDayLabel(value, locale) {
  try {
    const d = parseDate(value) || new Date();
    const todayKey = istanbulDateKey(new Date());
    if (istanbulDateKey(d) === todayKey) {
      return locale === 'en' ? 'Today' : 'Bugün';
    }
    return d.toLocaleDateString(locale === 'en' ? 'en-GB' : 'tr-TR', {
      timeZone: 'Europe/Istanbul',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return locale === 'en' ? 'Today' : 'Bugün';
  }
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function isTopicEcho(body) {
  const t = String(body || '').trim();
  return /^(Konu|Topic)\s*:\s*.+/i.test(t);
}

function playNotifySound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.3);
    setTimeout(() => ctx.close().catch(() => {}), 400);
  } catch {
    /* ignore */
  }
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.2L4 17.2V4h16v12z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z" />
    </svg>
  );
}

function MinimizeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function AttachIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16.5 6.5v8.25a4.5 4.5 0 1 1-9 0V6.25a2.75 2.75 0 0 1 5.5 0v7.5a1 1 0 1 1-2 0V7h-1.5v6.75a2.5 2.5 0 1 0 5 0V6.25a4.25 4.25 0 1 0-8.5 0v8.5a5.75 5.75 0 1 0 11.5 0V6.5H16.5z" />
    </svg>
  );
}

function AgentIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );
}

function AgentFace({ src, spacer = false }) {
  if (spacer) {
    return <div className="vb-ze__agent-face vb-ze__agent-face--spacer" aria-hidden="true" />;
  }
  return (
    <div className="vb-ze__agent-face" aria-hidden="true">
      {src ? <img src={src} alt="" /> : <AgentIcon />}
    </div>
  );
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
    </svg>
  );
}

function EndIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm3.7 13.3a1 1 0 0 1-1.4 1.4L12 13.41l-2.3 2.3a1 1 0 0 1-1.4-1.42L10.59 12 8.3 9.7A1 1 0 1 1 9.7 8.3L12 10.59l2.3-2.3a1 1 0 0 1 1.4 1.42L13.41 12l2.29 2.3z" />
    </svg>
  );
}

export default function SupportMessenger({ onRegister } = {}) {
  const { token, user } = useAuth();
  const { locale } = useLocale();
  const { logo } = useSiteContent();
  const isMobile = useIsMobile();
  // Mobile: guests see floating launcher; logged-in users open chat from Hesabım only.
  const showLauncher = !isMobile || !(token || user);
  const guestKey = useMemo(() => ensureGuestKey(), []);
  const agentName = locale === 'en' ? 'VurgunBet Support' : 'VurgunBet Destek';
  const brandLogoUrl = logo?.mode === 'image' && logo?.imageUrl ? logo.imageUrl : null;

  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState(agentName);
  const [operatorAvatar, setOperatorAvatar] = useState(null);
  const [threadId, setThreadId] = useState(() => loadThreadId());
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [booting, setBooting] = useState(false);
  const [prechat, setPrechat] = useState(() => loadPrechat());
  const [followupDone, setFollowupDone] = useState(() => loadFollowupDone());
  const [pendingFiles, setPendingFiles] = useState([]);
  const [attachError, setAttachError] = useState('');
  const [typing, setTyping] = useState(false);
  const [holdCanned, setHoldCanned] = useState(false);
  const [scriptTyping, setScriptTyping] = useState(false);
  const [introShown, setIntroShown] = useState(0);
  const [catChipsReady, setCatChipsReady] = useState(false);
  const [followShown, setFollowShown] = useState(0);
  const [followChipsReady, setFollowChipsReady] = useState(false);
  const [soundOn, setSoundOn] = useState(() => loadSoundEnabled());
  const [menuOpen, setMenuOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [startedAt, setStartedAt] = useState(() => new Date());
  const [script, setScript] = useState(null);
  const bodyRef = useRef(null);
  const fileRef = useRef(null);
  const lastIdRef = useRef(0);
  const openRef = useRef(false);
  const soundOnRef = useRef(soundOn);
  const previewUrlsRef = useRef([]);
  const typingTimerRef = useRef(null);
  const cannedTimerRef = useRef(null);
  const seqTimerRef = useRef(null);
  const seqGenRef = useRef(0);
  const knownAgentIdsRef = useRef(new Set());
  const lastActivityRef = useRef(loadActivityAt());
  const endChatRef = useRef(async () => {});
  const followTopicSeqRef = useRef('');
  const introSeqDoneRef = useRef(false);

  const topics = useMemo(() => {
    const cats = Array.isArray(script?.categories) ? script.categories : null;
    if (cats?.length) {
      return cats
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((c) => ({
          value: c.value,
          label: locale === 'en' ? c.label_en || c.label_tr || c.value : c.label_tr || c.label_en || c.value,
        }));
    }
    return locale === 'en' ? TOPICS_EN : TOPICS_TR;
  }, [locale, script]);

  const introLines = useMemo(() => {
    const bubbles = locale === 'en' ? script?.welcome_bubbles_en : script?.welcome_bubbles_tr;
    const fromScript = filterIntroBubbles(Array.isArray(bubbles) ? bubbles : []);
    if (fromScript.length) return fromScript;
    return locale === 'en' ? INTRO_EN : INTRO_TR;
  }, [locale, script]);

  const topicLabelSet = useMemo(() => {
    const set = new Set();
    for (const t of topics) {
      if (t?.label) set.add(String(t.label).trim());
    }
    for (const t of TOPICS_TR) set.add(t.label);
    for (const t of TOPICS_EN) set.add(t.label);
    return set;
  }, [topics]);

  const topic = prechat?.topic || '';
  const followCfg = useMemo(() => {
    const cats = Array.isArray(script?.categories) ? script.categories : null;
    if (cats?.length && topic) {
      const hit = cats.find((c) => c.value === topic);
      if (hit) return hit;
    }
    return topic ? FOLLOWUPS[topic] || FOLLOWUPS.account : null;
  }, [script, topic]);

  const followPrompt = followCfg
    ? locale === 'en'
      ? followCfg.prompt_en || followCfg.prompt_tr || ''
      : followCfg.prompt_tr || followCfg.prompt_en || ''
    : '';
  const followOptions = followCfg
    ? locale === 'en'
      ? followCfg.options_en || followCfg.options_tr || []
      : followCfg.options_tr || followCfg.options_en || []
    : [];
  const followAutoReply = followCfg
    ? locale === 'en'
      ? String(followCfg.auto_reply_en || followCfg.auto_reply_tr || '').trim()
      : String(followCfg.auto_reply_tr || followCfg.auto_reply_en || '').trim()
    : '';

  const namePrompt =
    (locale === 'en' ? script?.name_prompt_en : script?.name_prompt_tr) ||
    (locale === 'en' ? 'What is your name?' : 'Adınız nedir?');
  const emailPrompt =
    (locale === 'en' ? script?.email_prompt_en : script?.email_prompt_tr) ||
    (locale === 'en' ? 'What is your email address?' : 'E-posta adresiniz nedir?');

  const nameVal = prechat?.name || String(user?.name || user?.username || '').trim();
  const emailVal = prechat?.email || String(user?.email || '').trim();

  // Collect contact info BEFORE follow-up escalates to Telegram operators.
  // Otherwise guests stay on name/email while an agent already replied — feels "disconnected".
  let step = 'category';
  if (!topic) step = 'category';
  else if (!nameVal) step = 'name';
  else if (!emailVal || !isValidEmail(emailVal)) step = 'email';
  else if (!followupDone) step = 'followup';
  else step = 'chat';

  const chatReady = step === 'chat';
  const composerOpen = true;

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    soundOnRef.current = soundOn;
  }, [soundOn]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      previewUrlsRef.current = [];
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (cannedTimerRef.current) clearTimeout(cannedTimerRef.current);
      if (seqTimerRef.current) clearTimeout(seqTimerRef.current);
      seqGenRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const scrollBottom = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const touchActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    saveActivityAt(now);
  }, []);

  const waitSeq = useCallback((ms, gen) => new Promise((resolve) => {
    if (seqTimerRef.current) clearTimeout(seqTimerRef.current);
    seqTimerRef.current = setTimeout(() => {
      seqTimerRef.current = null;
      resolve(gen === seqGenRef.current);
    }, ms);
  }), []);

  const stopTyping = useCallback(() => {
    setTyping(false);
    setHoldCanned(false);
    setScriptTyping(false);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (cannedTimerRef.current) {
      clearTimeout(cannedTimerRef.current);
      cannedTimerRef.current = null;
    }
    if (seqTimerRef.current) {
      clearTimeout(seqTimerRef.current);
      seqTimerRef.current = null;
    }
    seqGenRef.current += 1;
  }, []);

  const playCannedReveal = useCallback((ms = SCRIPT_TYPING_MS) => {
    if (cannedTimerRef.current) clearTimeout(cannedTimerRef.current);
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    setHoldCanned(true);
    setTyping(true);
    cannedTimerRef.current = setTimeout(() => {
      setTyping(false);
      setHoldCanned(false);
      cannedTimerRef.current = null;
    }, ms);
  }, []);

  const idleMinutes = useMemo(() => {
    const n = Number(script?.idle_timeout_minutes);
    if (Number.isFinite(n) && n >= 5 && n <= 180) return Math.round(n);
    return DEFAULT_IDLE_MINUTES;
  }, [script]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/support-chat/config?locale=${locale === 'en' ? 'en' : 'tr'}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        setEnabled(Boolean(data.enabled));
        if (data.title) setTitle(data.title);
        if (data.script && typeof data.script === 'object') setScript(data.script);
        const photo = data.operatorAvatar || data.agentPhoto || null;
        setOperatorAvatar(typeof photo === 'string' && photo.trim() ? photo.trim() : null);
      } catch {
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const mergeMessages = useCallback((incoming, { reset = false } = {}) => {
    setMessages((prev) => {
      const base = reset ? [] : prev;
      const map = new Map(base.map((m) => [m.id, m]));
      let addedAgent = 0;
      let heardAgent = false;
      let sawFresh = false;
      for (const m of incoming || []) {
        if (isTopicEcho(m.body) && !m.attachment && m.meta?.kind === 'prechat') continue;
        if (isTopicEcho(m.body) && !m.attachment && !m.meta?.attachment) continue;
        const isNew = !map.has(m.id);
        if (isNew && m.sender_type === 'agent') {
          heardAgent = true;
          if (!knownAgentIdsRef.current.has(m.id)) {
            knownAgentIdsRef.current.add(m.id);
            if (!openRef.current) addedAgent += 1;
            else if (soundOnRef.current && !reset) playNotifySound();
          }
        }
        if (isNew && !reset) sawFresh = true;
        map.set(m.id, m);
      }
      const next = [...map.values()].sort((a, b) => a.id - b.id);
      if (next.length) lastIdRef.current = next[next.length - 1].id;
      if (addedAgent) setUnread((u) => u + addedAgent);
      if (heardAgent && !reset) {
        setTyping(false);
        if (typingTimerRef.current) {
          clearTimeout(typingTimerRef.current);
          typingTimerRef.current = null;
        }
      }
      if (sawFresh) {
        const now = Date.now();
        lastActivityRef.current = now;
        saveActivityAt(now);
      }
      return next;
    });
  }, []);

  const applyPrechatState = useCallback((next) => {
    const cleaned = {
      name: String(next?.name || '').trim(),
      email: String(next?.email || '').trim(),
      topic: String(next?.topic || '').trim(),
    };
    setPrechat(cleaned);
    savePrechat(cleaned);
    return cleaned;
  }, []);

  const markFollowup = useCallback((done) => {
    setFollowupDone(done);
    saveFollowupDone(done);
  }, []);

  const ensureSession = useCallback(async (prechatOverride = null, { forceNew = false } = {}) => {
    setBooting(true);
    try {
      const pc = prechatOverride || prechat || loadPrechat();
      const seed = {
        name: pc?.name || String(user?.name || user?.username || '').trim() || undefined,
        email: pc?.email || String(user?.email || '').trim() || undefined,
        topic: pc?.topic || undefined,
      };

      const postSession = async (asNew) => {
        const res = await fetch('/api/support-chat/session', {
          method: 'POST',
          headers: authHeaders(token, guestKey),
          body: JSON.stringify({
            guest_key: guestKey,
            thread_id: asNew ? undefined : threadId || loadThreadId() || undefined,
            locale: locale === 'en' ? 'en' : 'tr',
            prechat: seed.name || seed.email || seed.topic ? seed : undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'session');
        return data;
      };

      let data = await postSession(forceNew);
      let restored = {
        name: data.prechat?.name || seed.name || '',
        email: data.prechat?.email || seed.email || '',
        topic: data.prechat?.topic || seed.topic || '',
      };
      let msgs = Array.isArray(data.messages) ? data.messages : [];
      const hasUser = msgs.some((m) => m.sender_type === 'user');
      const idleLimitMs = idleMinutes * 60 * 1000;
      const idleExpired = Date.now() - lastActivityRef.current >= idleLimitMs;
      const unrestorable = hasUser && !restored.topic;

      if (!forceNew && (idleExpired || unrestorable) && (data.thread_id || threadId || loadThreadId())) {
        const oldTid = data.thread_id || threadId || loadThreadId();
        if (oldTid) {
          try {
            await fetch('/api/support-chat/end', {
              method: 'POST',
              headers: authHeaders(token, guestKey),
              body: JSON.stringify({
                thread_id: oldTid,
                guest_key: guestKey,
                locale: locale === 'en' ? 'en' : 'tr',
              }),
            });
          } catch {
            /* ignore */
          }
        }
        setMessages([]);
        lastIdRef.current = 0;
        knownAgentIdsRef.current = new Set();
        setThreadId('');
        saveThreadId('');
        markFollowup(false);
        introSeqDoneRef.current = false;
        followTopicSeqRef.current = '';
        setIntroShown(0);
        setCatChipsReady(false);
        setFollowShown(0);
        setFollowChipsReady(false);
        setScriptTyping(false);
        restored = {
          name: restored.name || String(user?.name || user?.username || '').trim() || '',
          email: restored.email || String(user?.email || '').trim() || '',
          topic: '',
        };
        applyPrechatState(restored);
        const now = Date.now();
        lastActivityRef.current = now;
        saveActivityAt(now);
        seed.topic = undefined;
        data = await postSession(true);
        restored = {
          name: data.prechat?.name || restored.name || '',
          email: data.prechat?.email || restored.email || '',
          topic: data.prechat?.topic || '',
        };
        msgs = Array.isArray(data.messages) ? data.messages : [];
      }

      setEnabled(data.enabled !== false);
      if (data.title) setTitle(data.title);
      if (data.thread_id) {
        setThreadId(data.thread_id);
        saveThreadId(data.thread_id);
      }
      if (restored.name || restored.email || restored.topic) applyPrechatState(restored);
      else if (seed.name || seed.email || seed.topic) applyPrechatState(seed);

      const followDone = inferFollowupDone(msgs, restored, topicLabelSet);
      markFollowup(followDone);
      if (restored.topic || followDone || msgs.some((m) => m.sender_type === 'user')) {
        introSeqDoneRef.current = true;
      } else {
        introSeqDoneRef.current = false;
      }

      knownAgentIdsRef.current = new Set(
        msgs.filter((m) => m.sender_type === 'agent').map((m) => m.id),
      );
      const first = msgs[0];
      if (first?.created_at) setStartedAt(new Date(first.created_at));
      else setStartedAt(new Date());
      mergeMessages(msgs, { reset: true });
      requestAnimationFrame(scrollBottom);
      return data.thread_id;
    } finally {
      setBooting(false);
    }
  }, [
    applyPrechatState,
    guestKey,
    idleMinutes,
    locale,
    markFollowup,
    mergeMessages,
    prechat,
    scrollBottom,
    threadId,
    token,
    topicLabelSet,
    user,
  ]);

  const updatePrechat = useCallback(async (partial, { asMessage = null } = {}) => {
    let tid = threadId || loadThreadId();
    if (!tid) tid = await ensureSession();
    const res = await fetch('/api/support-chat/prechat', {
      method: 'POST',
      headers: authHeaders(token, guestKey),
      body: JSON.stringify({
        thread_id: tid,
        guest_key: guestKey,
        locale: locale === 'en' ? 'en' : 'tr',
        ...partial,
        message: asMessage || undefined,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'prechat');
    if (data.prechat) applyPrechatState(data.prechat);
    else applyPrechatState({ ...(prechat || {}), ...partial });
    if (data.message) mergeMessages([data.message]);
    if (data.messages?.length) mergeMessages(data.messages);
    requestAnimationFrame(scrollBottom);
    return data;
  }, [applyPrechatState, ensureSession, guestKey, locale, mergeMessages, prechat, scrollBottom, threadId, token]);

  const sendUserText = useCallback(async (text) => {
    let tid = threadId || loadThreadId();
    if (!tid) tid = await ensureSession();
    const res = await fetch('/api/support-chat/messages', {
      method: 'POST',
      headers: authHeaders(token, guestKey),
      body: JSON.stringify({
        thread_id: tid,
        guest_key: guestKey,
        body: text,
        locale: locale === 'en' ? 'en' : 'tr',
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'send');
    if (data.message) mergeMessages([data.message]);
    requestAnimationFrame(scrollBottom);
    return data;
  }, [ensureSession, guestKey, locale, mergeMessages, scrollBottom, threadId, token]);

  const poll = useCallback(async () => {
    const tid = threadId || loadThreadId();
    if (!tid) return;
    try {
      const after = lastIdRef.current || 0;
      const res = await fetch(
        `/api/support-chat/messages?thread_id=${encodeURIComponent(tid)}&after=${after}&guest_key=${encodeURIComponent(guestKey)}&locale=${locale === 'en' ? 'en' : 'tr'}`,
        { headers: authHeaders(token, guestKey) },
      );
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (data.messages?.length) {
        mergeMessages(data.messages);
        if (openRef.current) requestAnimationFrame(scrollBottom);
      }
      if (data.prechat) applyPrechatState(data.prechat);
    } catch {
      /* ignore */
    }
  }, [applyPrechatState, guestKey, locale, mergeMessages, scrollBottom, threadId, token]);

  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, poll]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      scrollBottom();
    }
  }, [open, scrollBottom, messages.length, step, typing, scriptTyping, introShown, followShown]);

  useEffect(() => {
    if (!open || booting) return undefined;
    if (topic || followupDone || step !== 'category') {
      setIntroShown(introLines.length);
      setCatChipsReady(true);
      setScriptTyping(false);
      introSeqDoneRef.current = true;
      return undefined;
    }
    if (introSeqDoneRef.current) {
      setIntroShown(introLines.length);
      setCatChipsReady(true);
      return undefined;
    }

    const gen = seqGenRef.current + 1;
    seqGenRef.current = gen;
    let cancelled = false;

    (async () => {
      setCatChipsReady(false);
      setIntroShown(0);
      for (let i = 0; i < introLines.length; i += 1) {
        if (cancelled || gen !== seqGenRef.current) return;
        setScriptTyping(true);
        requestAnimationFrame(scrollBottom);
        const okTyping = await waitSeq(SCRIPT_TYPING_MS, gen);
        if (!okTyping || cancelled) return;
        setScriptTyping(false);
        setIntroShown(i + 1);
        requestAnimationFrame(scrollBottom);
        if (i + 1 < introLines.length) {
          const okGap = await waitSeq(SCRIPT_GAP_MS, gen);
          if (!okGap || cancelled) return;
        }
      }
      if (cancelled || gen !== seqGenRef.current) return;
      introSeqDoneRef.current = true;
      setCatChipsReady(true);
      requestAnimationFrame(scrollBottom);
    })();

    return () => {
      cancelled = true;
      if (seqGenRef.current === gen) seqGenRef.current += 1;
      setScriptTyping(false);
    };
  }, [open, booting, topic, followupDone, step, introLines, waitSeq, scrollBottom]);

  useEffect(() => {
    if (!open || booting || step !== 'followup') return undefined;
    const key = `${topic}::${followAutoReply}::${followPrompt}`;
    if (followTopicSeqRef.current === key) return undefined;

    const bubbleCount = (followAutoReply ? 1 : 0) + (followPrompt ? 1 : 0);
    followTopicSeqRef.current = key;
    if (!bubbleCount) {
      setFollowShown(0);
      setFollowChipsReady(true);
      setScriptTyping(false);
      return undefined;
    }

    const gen = seqGenRef.current + 1;
    seqGenRef.current = gen;
    let cancelled = false;

    (async () => {
      setFollowChipsReady(false);
      setFollowShown(0);
      for (let i = 0; i < bubbleCount; i += 1) {
        if (cancelled || gen !== seqGenRef.current) return;
        setScriptTyping(true);
        requestAnimationFrame(scrollBottom);
        const okTyping = await waitSeq(SCRIPT_TYPING_MS, gen);
        if (!okTyping || cancelled) return;
        setScriptTyping(false);
        setFollowShown(i + 1);
        requestAnimationFrame(scrollBottom);
        if (i + 1 < bubbleCount) {
          const okGap = await waitSeq(SCRIPT_GAP_MS, gen);
          if (!okGap || cancelled) return;
        }
      }
      if (cancelled || gen !== seqGenRef.current) return;
      setFollowChipsReady(true);
      requestAnimationFrame(scrollBottom);
    })();

    return () => {
      cancelled = true;
      if (seqGenRef.current === gen) seqGenRef.current += 1;
      setScriptTyping(false);
    };
  }, [open, booting, step, topic, followAutoReply, followPrompt, waitSeq, scrollBottom]);

  const openMessenger = useCallback(async () => {
    setOpen(true);
    setUnread(0);
    try {
      await ensureSession();
    } catch {
      /* keep UI */
    }
  }, [ensureSession]);

  useEffect(() => {
    const onOpen = () => {
      openMessenger();
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [openMessenger]);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      setMenuOpen(false);
      return;
    }
    await openMessenger();
  };

  /** BlindLuck: category chip → local label bubble; topic stored silently (no Telegram) */
  const selectTopic = async (topicValue) => {
    if (sending || !topicValue) return;
    const label = topics.find((t) => t.value === topicValue)?.label || topicValue;
    setSending(true);
    touchActivity();
    try {
      const name = prechat?.name || String(user?.name || user?.username || '').trim();
      const email = prechat?.email || String(user?.email || '').trim();
      stopTyping();
      followTopicSeqRef.current = '';
      setFollowShown(0);
      setFollowChipsReady(false);
      await updatePrechat(
        { topic: topicValue, name: name || undefined, email: email || undefined },
        { asMessage: label },
      );
      markFollowup(false);
    } catch {
      /* ignore */
    } finally {
      setSending(false);
    }
  };

  const openRegisterFromSupport = useCallback(() => {
    setOpen(false);
    setMenuOpen(false);
    try {
      if (typeof onRegister === 'function') onRegister();
      else window.dispatchEvent(new CustomEvent(OPEN_REGISTER_EVENT));
    } catch {
      window.dispatchEvent(new CustomEvent(OPEN_REGISTER_EVENT));
    }
  }, [onRegister]);

  const isOpenRegisterOption = useCallback((optionText) => {
    const raw = String(optionText || '').trim().toLowerCase();
    if (!raw) return false;
    if (OPEN_REGISTER_OPTIONS.has(String(optionText || '').trim())) return true;
    return (
      raw.includes('kayıt sayfasını aç')
      || raw.includes('kayit sayfasini ac')
      || raw.includes('open the registration page')
      || raw.includes('registration page')
    );
  }, []);

  const selectFollowup = async (optionText) => {
    if (sending || !optionText) return;
    setSending(true);
    touchActivity();
    const openReg = isOpenRegisterOption(optionText);
    if (openReg) {
      // Open register immediately — do not wait on chat send.
      openRegisterFromSupport();
    }
    try {
      markFollowup(true);
      await sendUserText(optionText);
      if (openReg) return;
      stopTyping();
    } catch (err) {
      markFollowup(false);
      setAttachError(err?.message || (locale === 'en' ? 'Could not connect' : 'Bağlantı kurulamadı'));
    } finally {
      setSending(false);
    }
  };

  const clearPendingFiles = () => {
    previewUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    previewUrlsRef.current = [];
    setPendingFiles([]);
  };

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (!list.length) return;
    setAttachError('');
    const next = [];
    for (const file of list) {
      if (!ALLOWED_MIME.has(file.type)) {
        setAttachError(locale === 'en' ? 'Only photos (jpg/png/webp/gif) and PDF are allowed' : 'Yalnızca fotoğraf (jpg/png/webp/gif) ve PDF yükleyebilirsiniz');
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setAttachError(locale === 'en' ? 'File must be 10MB or smaller' : 'Dosya en fazla 10MB olabilir');
        continue;
      }
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
      if (previewUrl) previewUrlsRef.current.push(previewUrl);
      next.push({ file, previewUrl, id: `${file.name}-${file.size}-${file.lastModified}` });
    }
    if (next.length) setPendingFiles((prev) => [...prev, ...next].slice(0, 5));
  };

  const removePending = (id) => {
    setPendingFiles((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
        previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== target.previewUrl);
      }
      return prev.filter((p) => p.id !== id);
    });
  };

  const send = async () => {
    const text = draft.trim();
    if (sending) return;

    if (step === 'category' && text) {
      setSending(true);
      touchActivity();
      try {
        await updatePrechat({
          topic: 'account',
          name: nameVal || undefined,
          email: emailVal || undefined,
        });
        // Free-text from category: only escalate if contact info already known
        if (nameVal && emailVal && isValidEmail(emailVal)) {
          markFollowup(true);
          await sendUserText(text);
        } else {
          markFollowup(false);
        }
        setDraft('');
        stopTyping();
      } catch (err) {
        setAttachError(err?.message || (locale === 'en' ? 'Could not send' : 'Gönderilemedi'));
      } finally {
        setSending(false);
      }
      return;
    }

    if (step === 'followup' && text) {
      setSending(true);
      touchActivity();
      try {
        markFollowup(true);
        await sendUserText(text);
        setDraft('');
        stopTyping();
      } catch (err) {
        markFollowup(false);
        setAttachError(err?.message || (locale === 'en' ? 'Could not send' : 'Gönderilemedi'));
      } finally {
        setSending(false);
      }
      return;
    }

    if (step === 'name') {
      if (!text || text.length < 2) return;
      setSending(true);
      touchActivity();
      try {
        await updatePrechat({ name: text }, { asMessage: text });
        setDraft('');
        stopTyping();
      } catch {
        /* ignore */
      } finally {
        setSending(false);
      }
      return;
    }

    if (step === 'email') {
      if (!isValidEmail(text)) {
        setAttachError(locale === 'en' ? 'Please enter a valid email' : 'Geçerli bir e-posta girin');
        return;
      }
      setSending(true);
      touchActivity();
      try {
        await updatePrechat({ email: text.trim() }, { asMessage: text.trim() });
        setDraft('');
        setAttachError('');
        stopTyping();
      } catch {
        /* ignore */
      } finally {
        setSending(false);
      }
      return;
    }

    if (!chatReady) return;
    if (!text && !pendingFiles.length) return;

    setSending(true);
    touchActivity();
    try {
      let tid = threadId;
      if (!tid) tid = await ensureSession();

      if (pendingFiles.length) {
        const fd = new FormData();
        fd.append('thread_id', tid);
        fd.append('guest_key', guestKey);
        fd.append('locale', locale === 'en' ? 'en' : 'tr');
        if (text) fd.append('body', text);
        for (const item of pendingFiles) fd.append('files', item.file);

        const res = await fetch('/api/support-chat/attachments', {
          method: 'POST',
          headers: authHeaders(token, guestKey, false),
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'upload');
        setDraft('');
        clearPendingFiles();
        if (data.messages?.length) mergeMessages(data.messages);
        else if (data.message) mergeMessages([data.message]);
      } else {
        await sendUserText(text);
        setDraft('');
      }
      stopTyping();
      requestAnimationFrame(scrollBottom);
    } catch (err) {
      setAttachError(err?.message || (locale === 'en' ? 'Could not send' : 'Gönderilemedi'));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      saveSoundEnabled(next);
      return next;
    });
  };

  const endChat = async () => {
    setMenuOpen(false);
    stopTyping();
    clearPendingFiles();
    clearActivityAt();
    lastActivityRef.current = Date.now();
    const oldTid = threadId || loadThreadId();
    if (oldTid) {
      try {
        await fetch('/api/support-chat/end', {
          method: 'POST',
          headers: authHeaders(token, guestKey),
          body: JSON.stringify({
            thread_id: oldTid,
            guest_key: guestKey,
            locale: locale === 'en' ? 'en' : 'tr',
          }),
        });
      } catch {
        /* ignore */
      }
    }
    setMessages([]);
    lastIdRef.current = 0;
    knownAgentIdsRef.current = new Set();
    setThreadId('');
    saveThreadId('');
    markFollowup(false);
    introSeqDoneRef.current = false;
    followTopicSeqRef.current = '';
    setIntroShown(0);
    setCatChipsReady(false);
    setFollowShown(0);
    setFollowChipsReady(false);
    setScriptTyping(false);
    const keep = {
      name: prechat?.name || String(user?.name || user?.username || '').trim() || '',
      email: prechat?.email || String(user?.email || '').trim() || '',
      topic: '',
    };
    applyPrechatState(keep);
    setDraft('');
    setAttachError('');
    setStartedAt(new Date());
    try {
      await ensureSession(keep, { forceNew: true });
    } catch {
      /* ignore */
    }
  };

  endChatRef.current = endChat;

  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => {
      const hasProgress = Boolean(topic || followupDone || messages.length || loadThreadId());
      if (!hasProgress) return;
      const limitMs = idleMinutes * 60 * 1000;
      if (Date.now() - lastActivityRef.current < limitMs) return;
      void endChatRef.current();
    };
    tick();
    const id = setInterval(tick, IDLE_CHECK_MS);
    return () => clearInterval(id);
  }, [enabled, followupDone, idleMinutes, messages.length, topic]);

  if (!enabled) return null;

  const visibleMessages = messages.filter(
    (m) => !(isTopicEcho(m.body) && !m.attachment && !(m.body && m.body.length > 80)),
  );

  // Hide DB welcome — BlindLuck-style 3 intro bubbles are rendered client-side
  const serverWelcomeIds = new Set();
  if (visibleMessages.length) {
    const firstAgent = visibleMessages.find((m) => m.sender_type === 'agent' && !m.attachment);
    if (firstAgent) serverWelcomeIds.add(firstAgent.id);
  }
  const threadMessages = visibleMessages.filter((m) => !serverWelcomeIds.has(m.id));

  const showIntro = !booting;
  const showCategoryChips = step === 'category' && catChipsReady;
  const showFollowAuto = step === 'followup' && Boolean(followAutoReply) && followShown >= 1;
  const showFollowPrompt =
    step === 'followup' &&
    Boolean(followPrompt) &&
    followShown >= (followAutoReply ? 2 : 1);
  const showFollowupChips = step === 'followup' && followChipsReady && !holdCanned;
  const showNamePrompt = step === 'name' && !holdCanned;
  const showEmailPrompt = step === 'email' && !holdCanned;
  const showTyping = Boolean(scriptTyping || (typing && holdCanned));

  const agentFaceSrc = operatorAvatar || brandLogoUrl || null;

  const placeholder =
    step === 'name'
      ? locale === 'en'
        ? 'Your name…'
        : 'Adınız…'
      : step === 'email'
        ? locale === 'en'
          ? 'Email…'
          : 'E-posta…'
        : locale === 'en'
          ? 'Type a message'
          : 'Mesaj yazın';

  return (
    <div className={`vb-ze${open ? ' is-open' : ''}${showLauncher ? '' : ' vb-ze--account-only'}`} data-product="messenger">
      <div
        className={`vb-ze__frame${open ? ' is-open' : ''}`}
        role="dialog"
        aria-label={title}
        aria-hidden={!open}
      >
        <div className="vb-ze__header">
          <div className="vb-ze__avatar" aria-hidden="true">
            {agentFaceSrc ? <img src={agentFaceSrc} alt="" /> : <AgentIcon />}
          </div>
          <div className="vb-ze__header-text">
            <div className="vb-ze__title vb-ze__title--logo" aria-label="VurgunBet">
              {logo ? (
                <Logo
                  mode={logo.mode}
                  text={logo.text || 'VurgunBet'}
                  textSize={Math.min(Number(logo.textSize) || 18, 20)}
                  textStyle={logo.textStyle}
                  imageUrl={logo.imageUrl}
                  alt={logo.alt || 'VurgunBet'}
                />
              ) : (
                <span className="vb-ze__brand-fallback">VurgunBet</span>
              )}
            </div>
            <p className="vb-ze__subtitle">{title || agentName}</p>
          </div>
          <div className="vb-ze__header-actions">
            <button
              type="button"
              className="vb-ze__header-btn"
              onClick={endChat}
              aria-label={locale === 'en' ? 'End conversation' : 'Sohbeti bitir'}
              title={locale === 'en' ? 'End conversation' : 'Sohbeti bitir'}
            >
              <EndIcon />
            </button>
            <button
              type="button"
              className="vb-ze__header-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={locale === 'en' ? 'Options menu' : 'Seçenekler'}
              aria-expanded={menuOpen}
            >
              <MoreIcon />
            </button>
            <button
              type="button"
              className="vb-ze__header-btn"
              onClick={() => { setOpen(false); setMenuOpen(false); }}
              aria-label={locale === 'en' ? 'Minimize' : 'Küçült'}
            >
              <MinimizeIcon />
            </button>
          </div>
          {menuOpen ? (
            <div className="vb-ze__menu" role="menu">
              <button type="button" className="vb-ze__menu-item vb-ze__menu-item--sound" role="menuitem" onClick={toggleSound}>
                <span className="vb-ze__menu-sound-text">
                  <strong>{soundOn ? (locale === 'en' ? 'Sound on' : 'Ses açık') : (locale === 'en' ? 'Sound off' : 'Ses kapalı')}</strong>
                  <small>{locale === 'en' ? 'Toggle sound notifications' : 'Ses bildirimlerini aç/kapat'}</small>
                </span>
                <span className={`vb-ze__switch${soundOn ? ' is-on' : ''}`} aria-hidden="true">
                  <i />
                </span>
              </button>
            </div>
          ) : null}
        </div>

        <div className="vb-ze__body" ref={bodyRef} onClick={() => menuOpen && setMenuOpen(false)}>
          {booting && !threadMessages.length && step === 'category' ? (
            <div className="vb-ze__disabled">{locale === 'en' ? 'Connecting…' : 'Bağlanıyor…'}</div>
          ) : null}

          <p className="vb-ze__privacy">
            {locale === 'en'
              ? 'This chat is recorded using a cloud service.'
              : 'Bu sohbet bulut hizmeti ile kaydedilir.'}
          </p>

          {!booting ? <div className="vb-ze__day">{formatDayLabel(startedAt, locale)}</div> : null}

          {/* BlindLuck-style multi-bubble intro (persists for the session) */}
          {showIntro ? (
            <>
              {introLines.slice(0, introShown).map((line, idx) => (
                <div key={`intro-${idx}`} className="vb-ze__row vb-ze__row--agent vb-ze__row--enter">
                  <AgentFace src={agentFaceSrc} spacer={idx !== introShown - 1} />
                  <div className="vb-ze__bubble-col">
                    {idx === 0 ? <div className="vb-ze__agent-name">{agentName}</div> : null}
                    <div className="vb-ze__bubble">{line}</div>
                  </div>
                </div>
              ))}
            </>
          ) : null}

          {showCategoryChips ? (
            <div className="vb-ze__replies vb-ze__row--enter" role="group" aria-label={locale === 'en' ? 'Categories' : 'Kategoriler'}>
              <div className="vb-ze__just-now">{locale === 'en' ? 'Just now' : 'Şimdi'}</div>
              <div className="vb-ze__choices">
                {topics.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    className="vb-ze__choice"
                    disabled={sending}
                    onClick={() => selectTopic(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {threadMessages.map((m) => {
            const att = m.attachment || m.meta?.attachment;
            const isUser = m.sender_type === 'user';
            return (
              <div
                key={m.id}
                className={`vb-ze__row vb-ze__row--${isUser ? 'user' : 'agent'} vb-ze__row--enter`}
              >
                {!isUser ? <AgentFace src={agentFaceSrc} /> : null}
                <div className="vb-ze__bubble-col">
                  {!isUser ? <div className="vb-ze__agent-name">{agentName}</div> : null}
                  <div className={`vb-ze__bubble${isUser ? ' vb-ze__bubble--pill' : ''}`}>
                    {att?.url && String(att.mime || '').startsWith('image/') ? (
                      <button
                        type="button"
                        className="vb-ze__att-image"
                        onClick={() => setLightbox({ url: att.url, alt: att.name || 'image' })}
                      >
                        <img src={att.url} alt={att.name || 'image'} />
                      </button>
                    ) : null}
                    {att?.url && !String(att.mime || '').startsWith('image/') ? (
                      <a className="vb-ze__att-file" href={att.url} target="_blank" rel="noreferrer">
                        PDF {att.name || 'dosya'}
                      </a>
                    ) : null}
                    {m.body && !(att && m.body === att.name) ? <div>{m.body}</div> : null}
                    <div className="vb-ze__meta">
                      {isUser
                        ? `${locale === 'en' ? 'Sent' : 'Gönderildi'} · ${formatTime(m.created_at)}`
                        : formatTime(m.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {showFollowAuto && !booting ? (
            <div className="vb-ze__row vb-ze__row--agent vb-ze__row--enter">
              <AgentFace src={agentFaceSrc} />
              <div className="vb-ze__bubble-col">
                <div className="vb-ze__agent-name">{agentName}</div>
                <div className="vb-ze__bubble">{followAutoReply}</div>
              </div>
            </div>
          ) : null}

          {showFollowPrompt && !booting ? (
            <div className="vb-ze__row vb-ze__row--agent vb-ze__row--enter">
              <AgentFace src={agentFaceSrc} />
              <div className="vb-ze__bubble-col">
                <div className="vb-ze__agent-name">{agentName}</div>
                <div className="vb-ze__bubble">{followPrompt}</div>
              </div>
            </div>
          ) : null}

          {showFollowupChips && !booting ? (
            <div className="vb-ze__replies vb-ze__replies--stack vb-ze__row--enter" role="group">
              <div className="vb-ze__just-now">{locale === 'en' ? 'Just now' : 'Şimdi'}</div>
              <div className="vb-ze__choices vb-ze__choices--stack">
                {followOptions.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`vb-ze__choice vb-ze__choice--wide${/^(Diğer|Other)$/i.test(opt) ? ' vb-ze__choice--muted' : ''}`}
                    disabled={sending}
                    onClick={() => selectFollowup(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {showNamePrompt && !booting ? (
            <div className="vb-ze__row vb-ze__row--agent vb-ze__row--enter">
              <AgentFace src={agentFaceSrc} />
              <div className="vb-ze__bubble-col">
                <div className="vb-ze__agent-name">{agentName}</div>
                <div className="vb-ze__bubble">{namePrompt}</div>
              </div>
            </div>
          ) : null}

          {showEmailPrompt && !booting ? (
            <div className="vb-ze__row vb-ze__row--agent vb-ze__row--enter">
              <AgentFace src={agentFaceSrc} />
              <div className="vb-ze__bubble-col">
                <div className="vb-ze__agent-name">{agentName}</div>
                <div className="vb-ze__bubble">{emailPrompt}</div>
              </div>
            </div>
          ) : null}

          {showTyping ? (
            <div className="vb-ze__row vb-ze__row--agent vb-ze__row--enter" aria-live="polite">
              <AgentFace src={agentFaceSrc} />
              <div className="vb-ze__bubble vb-ze__bubble--typing">
                <span className="vb-ze__typing-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                <span className="vb-ze__typing-label">{locale === 'en' ? 'Typing…' : 'Yazıyor…'}</span>
              </div>
            </div>
          ) : null}
        </div>

        {composerOpen ? (
          <div className="vb-ze__composer-wrap">
            {pendingFiles.length ? (
              <div className="vb-ze__previews">
                {pendingFiles.map((item) => (
                  <div key={item.id} className="vb-ze__preview">
                    {item.previewUrl ? (
                      <button
                        type="button"
                        className="vb-ze__preview-hit"
                        onClick={() => setLightbox({ url: item.previewUrl, alt: item.file?.name || 'preview' })}
                      >
                        <img src={item.previewUrl} alt="" />
                      </button>
                    ) : (
                      <span className="vb-ze__preview-pdf">PDF</span>
                    )}
                    <button type="button" className="vb-ze__preview-x" onClick={() => removePending(item.id)} aria-label="Remove">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            {attachError ? <p className="vb-ze__attach-error">{attachError}</p> : null}
            <div className="vb-ze__composer">
              {chatReady || step === 'followup' || step === 'category' ? (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                    multiple
                    hidden
                    onChange={onPickFiles}
                  />
                  <button
                    type="button"
                    className="vb-ze__attach"
                    onClick={() => {
                      if (!chatReady) {
                        setAttachError(locale === 'en' ? 'Select a category first' : 'Önce bir kategori seçin');
                        return;
                      }
                      fileRef.current?.click();
                    }}
                    aria-label={locale === 'en' ? 'Upload file' : 'Dosya yükle'}
                    disabled={sending}
                  >
                    <AttachIcon />
                  </button>
                </>
              ) : null}
              <textarea
                className="vb-ze__input"
                rows={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                aria-label={locale === 'en' ? 'Message' : 'Mesaj'}
              />
              <button
                type="button"
                className="vb-ze__send"
                onClick={send}
                disabled={sending || (!draft.trim() && !pendingFiles.length)}
                aria-label={locale === 'en' ? 'Send' : 'Gönder'}
              >
                <SendIcon />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {showLauncher ? (
        <button
          type="button"
          className={`vb-ze__launcher${open ? ' is-open' : ''}`}
          onClick={toggle}
          aria-label={open ? (locale === 'en' ? 'Close messaging' : 'Sohbeti kapat') : (locale === 'en' ? 'Open messaging' : 'Sohbeti aç')}
          aria-expanded={open}
        >
          <span className="vb-ze__launcher-icon vb-ze__launcher-icon--chat">
            <ChatIcon />
          </span>
          <span className="vb-ze__launcher-icon vb-ze__launcher-icon--close">
            <CloseIcon />
          </span>
          {unread > 0 && !open ? <span className="vb-ze__badge">{unread > 9 ? '9+' : unread}</span> : null}
        </button>
      ) : null}

      {lightbox ? (
        <div
          className="vb-ze__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={locale === 'en' ? 'Image preview' : 'Fotoğraf önizleme'}
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className="vb-ze__lightbox-close"
            aria-label={locale === 'en' ? 'Close preview' : 'Önizlemeyi kapat'}
            onClick={() => setLightbox(null)}
          >
            <CloseIcon />
          </button>
          <img
            src={lightbox.url}
            alt={lightbox.alt || ''}
            className="vb-ze__lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}
